import { deepmerge } from "@mui/utils"
import { alpha, createTheme, responsiveFontSizes } from "@mui/material/styles"

import type { CSSObject, Theme } from "@mui/material/styles"
import type {} from "@mui/x-date-pickers/themeAugmentation"

import { DEFAULT_THEME } from "helpers/utils/constants"
import {
  baseGreys,
  buildDeviceStatusPalette,
  buildExtrasPalette,
  buildQualityPalette,
  spanFrequencyRamp,
  spanStateColors,
} from "./helpers"
import type {
  DeviceStatusColor,
  ExtraColor,
  QualityColor,
  SpanStateColor,
} from "./helpers"

declare module "@mui/material/styles/createPalette" {
  interface TypeBackground {
    default: string
    paper: string
    light: string
  }

  export interface Palette {
    //password
    veryWeak: SimplePaletteColorOptions
    weak: SimplePaletteColorOptions
    fair: SimplePaletteColorOptions
    good: SimplePaletteColorOptions
    strong: SimplePaletteColorOptions
    //gauge
    gaugeBG: SimplePaletteColorOptions
    //conditional greys
    neutral: { [key: number]: string }
    //pie
    glow: SimplePaletteColorOptions
    // extras
    extras: {
      [key in ExtraColor]: PaletteColor
    }
    //angles
    alpha: SimplePaletteColorOptions
    beta: SimplePaletteColorOptions
    // device statuses
    deviceStatus: { [key in DeviceStatusColor]: PaletteColor }
    // quality levels
    quality: { [key in QualityColor]: PaletteColor }
    // limiting-span views: frequency ramp (ordered) plus the non-frequency states
    spanFrequency: { ramp: readonly string[] } & Record<SpanStateColor, string>
  }

  export interface PaletteOptions {
    //password
    veryWeak?: SimplePaletteColorOptions
    weak?: SimplePaletteColorOptions
    fair?: SimplePaletteColorOptions
    good?: SimplePaletteColorOptions
    strong?: SimplePaletteColorOptions
    //gauge
    gaugeBG?: SimplePaletteColorOptions
    //conditional greys
    neutral?: { [key: number]: string }
    //pie
    glow?: SimplePaletteColorOptions
    // extras
    extras?: {
      [key in ExtraColor]: PaletteColor
    }
    //angles
    alpha?: SimplePaletteColorOptions
    beta?: SimplePaletteColorOptions
    // device statuses
    deviceStatus?: { [key in DeviceStatusColor]: PaletteColor }
    // quality levels
    quality?: { [key in QualityColor]: PaletteColor }
    // limiting-span views: frequency ramp (ordered) plus the non-frequency states
    spanFrequency?: { ramp: readonly string[] } & Record<SpanStateColor, string>
  }
}

declare module "@mui/material/styles/createMixins" {
  interface Mixins {
    // Dialogs float over a backdrop. Menus, popovers and dropdowns float over the page.
    // Radii are px strings, since sx multiplies a bare number by shape.borderRadius.
    dialogSurface: CSSObject
    floatingSurface: CSSObject
  }
}

declare module "@mui/material/styles" {
  interface TypographyVariants {
    code: object
    overlineDense: object
  }
  interface TypographyVariantsOptions {
    code: object
    overlineDense: object
  }
}
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    code: true
    overlineDense: true
  }
}

const { palette } = createTheme()

const extrasPalette = buildExtrasPalette(palette)
const deviceStatusPalette = buildDeviceStatusPalette(palette)
const qualityPalette = buildQualityPalette(palette)

const PRIMARY_COLOR = "#009fe3"
const SECONDARY_COLOR = "#42B38E"

// MUI tints the hover of a picker item with action.hoverOpacity, which barely shows on
// our dark surfaces, and its selected hover darkens the fill instead of keeping it.
const pickerItemStates = (theme: Theme): CSSObject => ({
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.2),
  },
  "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus": {
    backgroundColor: theme.palette.primary.main,
  },
})

const baseTheme = createTheme({
  palette: {
    grey: {
      100: baseGreys[100],
      200: baseGreys[200],
      300: baseGreys[300],
      400: baseGreys[400],
      500: baseGreys[500],
      600: baseGreys[600],
      700: baseGreys[700],
      800: baseGreys[800],
      900: baseGreys[900],
    },
    background: {
      default: baseGreys[900],
      paper: baseGreys[800],
      light: baseGreys[700],
    },
    text: {
      primary: baseGreys[100],
      secondary: baseGreys[400],
      disabled: baseGreys[500],
    },
    neutral: {
      100: baseGreys[100],
      200: baseGreys[200],
      300: baseGreys[300],
      400: baseGreys[400],
      500: baseGreys[500],
      600: baseGreys[600],
      700: baseGreys[700],
      800: baseGreys[800],
      900: baseGreys[900],
    },
    action: {
      active: baseGreys[100],
      disabled: baseGreys[500],
      focus: "rgba(55, 65, 81, 0.12)",
      hover: "rgba(55, 65, 81, 0.04)",
      selected: "rgba(55, 65, 81, 0.08)",
      disabledBackground: "rgba(55, 65, 81, 0.12)",
    },
    primary: {
      main: PRIMARY_COLOR,
      dark: "#22303F",
      contrastText: "#fff",
    },
    secondary: {
      main: SECONDARY_COLOR,
    },
    warning: {
      main: "#F9B233", // yellow
      dark: "#28251E",
      light: "#FFF3DB",
    },
    error: {
      main: "#f03e3e", // red
      dark: "#281B21",
      light: "#FFE2E2",
    },
    success: {
      main: "#00C771", // light green
      dark: "#182D2D",
      light: "#E3FFF3",
    },
    info: {
      main: PRIMARY_COLOR,
      dark: "#112C39",
      light: "#DFF7FF",
    },
    deviceStatus: deviceStatusPalette,
    quality: qualityPalette,
    extras: extrasPalette,
    spanFrequency: { ramp: spanFrequencyRamp, ...spanStateColors },
    glow: {
      main: alpha(baseGreys[500], 0.5),
    },

    /* ---- Custom colors ---- */
    // password level
    veryWeak: palette.augmentColor({ color: { main: "#FF1744" }, name: "veryWeak" }),
    weak: palette.augmentColor({ color: { main: "#FF1744" }, name: "weak" }),
    fair: palette.augmentColor({ color: { main: "#FFC400" }, name: "fair" }),
    good: palette.augmentColor({ color: { main: "#9DE737" }, name: "good" }),
    strong: palette.augmentColor({ color: { main: "#00C771" }, name: "strong" }),

    // gauge level
    gaugeBG: palette.augmentColor({
      color: { main: "#7a7a7a24" },
      name: "gaugeBG",
    }),

    // angles
    alpha: palette.augmentColor({
      color: { main: PRIMARY_COLOR },
      name: "alpha",
    }),
    beta: palette.augmentColor({
      color: { main: SECONDARY_COLOR },
      name: "beta",
    }),
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ":root": {
          "--color-primary-main": PRIMARY_COLOR,
          "--color-secondary-main": SECONDARY_COLOR,
          "--color-background-paper": baseGreys[800],
          "--color-background-default": baseGreys[900],
          "--color-grey-100": baseGreys[100],
          "--color-grey-400": baseGreys[400],
          "--color-grey-600": baseGreys[600],
          "--color-grey-700": baseGreys[700],
          "--color-grey-800": baseGreys[800],
        },
        "*": {
          boxSizing: "border-box",
          margin: 0,
          padding: 0,
          "&::-webkit-scrollbar": {
            width: "6px",
            height: "6px",
          },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: "20px",
          },
          "&::-webkit-scrollbar-corner": {
            background: "inherit",
          },
        },
        html: {
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
        },
        body: {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
        },
        "#__next": {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
        },
        sizeSmall: {
          padding: "6px 16px",
        },
        sizeMedium: {
          padding: "8px 20px",
        },
        sizeLarge: {
          padding: "11px 24px",
        },
        textSizeSmall: {
          padding: "7px 12px",
        },
        textSizeMedium: {
          padding: "9px 16px",
        },
        textSizeLarge: {
          padding: "12px 16px",
        },
      },
    },

    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },

    // Each theme sets its surfaces once in mixins, and every floating paper reads them.
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => theme.mixins.dialogSurface,
      },
    },
    // Menu and Select menus render their paper through Popover.
    MuiPopover: {
      styleOverrides: {
        paper: ({ theme }) => theme.mixins.floatingSurface,
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: ({ theme }) => theme.mixins.floatingSurface,
      },
    },
    MuiPickersPopper: {
      styleOverrides: {
        paper: ({ theme }) => theme.mixins.floatingSurface,
      },
    },

    // Every date and time picker reads these, so they all share one look.
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => pickerItemStates(theme),
        today: ({ theme }) => ({
          // Same line as the picker panels around it.
          "&:not(.Mui-selected)": { borderColor: theme.palette.neutral[300] },
        }),
      },
    },
    MuiPickersMonth: {
      styleOverrides: {
        monthButton: ({ theme }) => pickerItemStates(theme),
      },
    },
    MuiPickersCalendarHeader: {
      styleOverrides: {
        label: ({ theme }) => ({
          fontSize: theme.typography.pxToRem(16),
          fontWeight: theme.typography.fontWeightBold,
          // Luxon's Spanish month names come lowercase.
          "&::first-letter": { textTransform: "uppercase" },
        }),
      },
    },
    MuiMultiSectionDigitalClockSection: {
      styleOverrides: {
        // The columns still scroll by wheel, touch and keys; a bar beside each one is noise.
        root: {
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        },
        item: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          ...pickerItemStates(theme),
        }),
      },
    },

    MuiCheckbox: {
      defaultProps: {
        color: "primary",
        disableRipple: true,
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: {
          fontSize: 12,
        },
      },
    },

    MuiFormControl: {
      defaultProps: {
        fullWidth: true,
      },
      styleOverrides: {
        root: {
          marginBottom: "1rem",
        },
      },
    },

    MuiTooltip: {
      defaultProps: {
        // MUI's 1.5s is gone before the finger is out of the way.
        leaveTouchDelay: 5000,
      },
      styleOverrides: {
        // Undo MUI's bigger text for touch-opened tooltips, keep the wider finger offset.
        touch: ({ theme }) => ({
          padding: "4px 8px",
          fontSize: theme.typography.pxToRem(11),
          lineHeight: "inherit",
          fontWeight: theme.typography.fontWeightMedium,
        }),
      },
    },
  },
})

baseTheme.typography = {
  ...baseTheme.typography,
  ..._createTypography(),
}

// MUI's shadows barely show on a dark page, so the edge carries most of the separation.
const darkSurfaceLine = alpha(baseTheme.palette.grey[100], 0.08)

const darkStateOpacity = { hover: 0.08, selected: 0.16, focus: 0.12 }

const darkTheme = createTheme(
  deepmerge(baseTheme, {
    mixins: {
      dialogSurface: {
        backgroundColor: baseTheme.palette.background.paper,
        border: `1px solid ${darkSurfaceLine}`,
        borderRadius: `${baseTheme.shape.borderRadius}px`,
        // A dialog over a table blends with it on the edge alone, so it gets a deeper shadow.
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.5)",
      },
      floatingSurface: {
        backgroundColor: baseTheme.palette.background.light,
        border: `1px solid ${darkSurfaceLine}`,
        borderRadius: `${baseTheme.shape.borderRadius}px`,
      },
    },
    palette: {
      mode: "dark",
      // MUI's dark state layers. The base values are dark grays meant for a light page.
      action: {
        active: baseTheme.palette.grey[100],
        disabled: baseTheme.palette.grey[500],
        hover: alpha(baseTheme.palette.grey[100], darkStateOpacity.hover),
        hoverOpacity: darkStateOpacity.hover,
        selected: alpha(baseTheme.palette.grey[100], darkStateOpacity.selected),
        selectedOpacity: darkStateOpacity.selected,
        focus: alpha(baseTheme.palette.grey[100], darkStateOpacity.focus),
        focusOpacity: darkStateOpacity.focus,
      },
      neutral: {
        100: baseTheme.palette.grey[900],
        200: baseTheme.palette.grey[800],
        300: baseTheme.palette.grey[700],
        400: baseTheme.palette.grey[600],
        500: baseTheme.palette.grey[500],
        600: baseTheme.palette.grey[400],
        700: baseTheme.palette.grey[300],
        800: baseTheme.palette.grey[200],
        900: baseTheme.palette.grey[100],
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          "*": {
            // Light bar on a dark track — the inverse reads as a gap in the UI.
            // One step above background.light, so it also shows on menus and popovers.
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: baseTheme.palette.grey[600],
              "&:hover": {
                backgroundColor: baseTheme.palette.grey[500],
              },
            },
            // Inset shadow so the track reads as a recessed groove the bar
            // rides in, rather than a flat stripe of a slightly darker grey.
            "&::-webkit-scrollbar-track": {
              backgroundColor: baseTheme.palette.grey[900],
              boxShadow: "inset 0 0 6px 2px rgba(0,0,0,0.6)",
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          // Dark mode lightens raised Paper with a white gradient, which pulls it off our palette.
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: darkSurfaceLine,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            backgroundColor: baseTheme.palette.primary.main,
            borderColor: baseTheme.palette.primary.main,
            "&:hover": {
              backgroundColor: alpha(baseTheme.palette.primary.main, 0.6),
            },
          },
        },
      },
    },
  }),
)

const lightTheme = createTheme(
  deepmerge(baseTheme, {
    mixins: {
      dialogSurface: {
        backgroundColor: "#ffffff",
        borderRadius: `${baseTheme.shape.borderRadius}px`,
      },
      floatingSurface: {
        backgroundColor: "#ffffff",
        borderRadius: `${baseTheme.shape.borderRadius}px`,
      },
    },
    palette: {
      mode: "light",
      text: {
        primary: baseTheme.palette.grey[800],
        secondary: baseTheme.palette.grey[700],
        disabled: baseTheme.palette.grey[500],
      },
      background: {
        default: baseTheme.palette.grey[100],
        paper: "#ffffff",
        light: "#ffffff",
      },
      action: {
        active: baseTheme.palette.grey[500],
        disabled: "rgba(55, 65, 81, 0.26)",
      },
      deviceStatus: {
        inactive: palette.augmentColor({
          color: {
            main: baseTheme.palette.grey[600],
            light: alpha(baseTheme.palette.grey[600], 0.15),
          },
        }),
        // notInstalled: palette.augmentColor({
        //   color: {
        //     main: baseTheme.palette.grey[500],
        //     light: alpha(baseTheme.palette.grey[500], 0.15),
        //   },
        // }),
      },
      glow: {
        main: "transparent",
      },
    },
  }),
)

interface IThemes {
  dark: Theme
  light: Theme
}

const themes: IThemes = {
  dark: responsiveFontSizes(darkTheme),
  light: responsiveFontSizes(lightTheme),
}

export const getTheme = (key: string) => {
  return themes[key as keyof IThemes]
    ? themes[key as keyof IThemes]
    : themes[DEFAULT_THEME]
}

export { darkTheme, lightTheme }

function _createTypography() {
  const fontSize = 13 // px
  const htmlFontSize = 16
  const coef = fontSize / 14
  const fontWeightLight = 300
  const fontWeightRegular = 400
  const fontWeightMedium = 500
  const fontWeightBold = 600
  const fontFamily = '"Inter", "Helvetica", "Arial", sans-serif'
  const fontFamilyMonospace = "'Roboto Mono', monospace"

  const pxToRem = (size: number) => `${(size / htmlFontSize) * coef}rem`
  // Sizes off the px value, without the coefficient. It would take the overline
  // variants a pixel down, and caps that small stop being legible.
  const exactRem = (size: number) => `${size / htmlFontSize}rem`

  const buildVariant = (
    fontWeight: number,
    size: number,
    lineHeight: number,
    casing?: object,
  ) => {
    return {
      fontFamily,
      fontWeight,
      fontSize: pxToRem(size),
      lineHeight: lineHeight / size,
      ...casing,
    }
  }

  const overlineBase = {
    fontFamily,
    fontWeight: fontWeightMedium,
    lineHeight: 1.4,
    textTransform: "uppercase" as const,
    // Caps this small need grey[400] to clear 4.5:1 over the card background.
    color: baseGreys[400],
  }

  const variants = {
    h1: buildVariant(fontWeightBold, 22, 28),
    h2: buildVariant(fontWeightBold, 20, 24),
    h3: buildVariant(fontWeightBold, 18, 22),
    h4: buildVariant(fontWeightBold, 16, 22),
    h5: buildVariant(fontWeightBold, 14, 20),
    h6: buildVariant(fontWeightMedium, 14, 18),
    body1: buildVariant(fontWeightRegular, fontSize, 18),
    body2: buildVariant(fontWeightRegular, 12, 18),
    // Structural labels: the weight comes from the tracking, not from the stroke.
    overline: { ...overlineBase, fontSize: exactRem(12), letterSpacing: "0.08em" },
    overlineDense: { ...overlineBase, fontSize: exactRem(10), letterSpacing: "0.09em" },
    caption: buildVariant(fontWeightRegular, 12, 16),
    button: buildVariant(fontWeightMedium, 14, 18),
    code: {
      ...buildVariant(fontWeightRegular, 14, 16),
      fontFamily: fontFamilyMonospace,
    },
  }

  return {
    htmlFontSize,
    fontFamily,
    fontFamilyMonospace,
    fontSize,
    fontWeightLight,
    fontWeightRegular,
    fontWeightMedium,
    fontWeightBold,
    ...variants,
  }
}
